import { getAccessTokenForGraph, getUserData } from "./auth.service";
import { MentionUser } from "../pages/discussions/components/richtext/types";

/**
 * Microsoft Graph User response type
 */
interface GraphUser {
  id: string;
  displayName: string;
  mail?: string;
  userPrincipalName: string;
  jobTitle?: string;
  department?: string;
  accountEnabled?: boolean;
  officeLocation?: string;
}

/**
 * Microsoft Graph API response for user list
 */
interface GraphUsersResponse {
  "@odata.context": string;
  "@odata.nextLink"?: string;
  value: GraphUser[];
}

/**
 * Cache for users to avoid repeated API calls
 */
let usersCache: MentionUser[] | null = null;
let cacheTimestamp: number | null = null;
const CACHE_DURATION = 5 * 60 * 1000; // 5 minutes

// The fetch in progress, shared so that startup, the mention input and Setup don't each start one.
let usersInFlight: Promise<MentionUser[]> | null = null;

/**
 * Fetch users from Microsoft Entra (Azure AD) via Microsoft Graph
 * Automatically filters to users in the current user's office location
 * @param forceRefresh - Force a refresh of the cache
 * @returns Promise with array of users formatted for mentions
 */
export async function getOrganizationUsers(forceRefresh: boolean = false): Promise<MentionUser[]> {
  // Check if cache is valid
  if (!forceRefresh && usersCache && cacheTimestamp && (Date.now() - cacheTimestamp < CACHE_DURATION)) {
    return usersCache;
  }
  if (!forceRefresh && usersInFlight) {
    return usersInFlight;
  }

  const request = fetchOrganizationUsers();
  usersInFlight = request;
  try {
    return await request;
  } finally {
    if (usersInFlight === request) {
      usersInFlight = null;
    }
  }
}

async function fetchOrganizationUsers(): Promise<MentionUser[]> {
  try {
    // Get current user's office location to filter by
    const currentUser = await getUserData();
    const userLocation = currentUser?.officeLocation;

    if (userLocation) {
      console.log(`Filtering users by office location: ${userLocation}`);
    } else {
      console.log('No office location found for current user, returning all users');
    }

    const accessToken = await getAccessTokenForGraph();

    // Graph does the filtering, so only the office's members are downloaded. Filtering on
    // officeLocation needs Graph's advanced query mode: the ConsistencyLevel header and
    // $count=true. Guests are left out: they have no office and aren't people to mention.
    const clauses = ["accountEnabled eq true", "userType eq 'Member'"];
    if (userLocation) {
      clauses.push(`officeLocation eq '${userLocation.replace(/'/g, "''")}'`);
    }
    const filterQuery = encodeURIComponent(clauses.join(" and "));

    // Select only the fields we need to reduce payload size
    let allUsers: GraphUser[] = [];
    let nextLink: string | undefined = `https://graph.microsoft.com/v1.0/users?$select=id,displayName,mail,userPrincipalName&$filter=${filterQuery}&$count=true&$top=999`;

    // Follow pagination links to get all users
    while (nextLink) {
      const response = await fetch(nextLink, {
        method: "GET",
        headers: {
          Authorization: `Bearer ${accessToken}`,
          "Content-Type": "application/json",
          ConsistencyLevel: "eventual",
        },
      });

      if (!response.ok) {
        const errorBody = await response.text();
        console.error("Graph API Error:", response.status, response.statusText, errorBody);
        throw new Error(`Failed to fetch users: ${response.status} ${response.statusText}`);
      }

      const data: GraphUsersResponse = await response.json();

      // Add this page of users to our collection
      allUsers = allUsers.concat(data.value);

      // Check if there's another page
      nextLink = data["@odata.nextLink"];

      // Log progress for large organizations
      if (nextLink) {
        console.log(`Fetched ${allUsers.length} users so far, fetching next page...`);
      }
    }

    console.log(`Fetched ${allUsers.length} active users from Microsoft Graph`);

    // Transform Graph API users to MentionUser format
    const users: MentionUser[] = allUsers.map((user) => ({
      id: user.mail || user.userPrincipalName, // Use email as ID
      display: user.displayName,
      email: user.mail || user.userPrincipalName,
    }));

    // Sort users alphabetically by display name
    users.sort((a, b) => a.display.localeCompare(b.display));

    // Update cache
    usersCache = users;
    cacheTimestamp = Date.now();

    return users;
  } catch (error) {
    console.error("Error fetching organization users:", error);
    throw error;
  }
}

/**
 * Search users by name or email
 * @param searchTerm - Search term to filter users
 * @returns Promise with filtered array of users
 */
export async function searchUsers(searchTerm: string): Promise<MentionUser[]> {
  const allUsers = await getOrganizationUsers();

  if (!searchTerm || searchTerm.trim() === "") {
    return allUsers;
  }

  const lowerSearchTerm = searchTerm.toLowerCase();

  return allUsers.filter(
    (user) =>
      user.display.toLowerCase().includes(lowerSearchTerm) ||
      (user.email && user.email.toLowerCase().includes(lowerSearchTerm))
  );
}

/**
 * Clear the users cache
 * Useful when you want to force a refresh on next fetch
 */
export function clearUsersCache(): void {
  usersCache = null;
  cacheTimestamp = null;
}
